/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file LicencesTable.tsx
 * @desc Created on Mon Apr 28 2025 20:44:52
 * @copyright All rights reserved @ Inarix
 */

import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Modal, ThemeProvider } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";

import themeInarix from "../StylesMuiInarix";
import { selectLocale } from "../../redux/slices/layout";
import {
  focusedLicense,
  LicensesFilters,
  selectLicencesAndOwners,
  selectLicenseLoading,
  selectModalLicense,
  selectOnlyUnassigned,
  setFilterModel,
  setPreselectedFilterField,
  setShowFilterForm,
} from "../../redux/slices/licenses/licenseList";
import { LicensePacksModal } from "./LicensePacksModal";

import SearchComponent from "../customFilterComponent/searchComponent";
import { CustomFilterButton } from "../customFilterComponent/customFilterButton";
import { DevicesStyles } from "../userDevices/DevicesStyles";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useLicenseColumns } from "./LicenceColumns";
import customFilterStyles from "../customFilterComponent/customFiltersStyles";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    /// custom filter component
    setFilterModel: (filterModel: any) => {
      dispatch(setFilterModel(filterModel));
    },

    setShowFilterForm: (show: boolean) => {
      dispatch(setShowFilterForm(show));
    },

    setPreselectedFilterField: (field: string | null) => {
      dispatch(setPreselectedFilterField(field));
    },
  };
};

export const LicencesTable: FC = () => {
  const locale = useSelector(selectLocale);
  const licenses = useSelector(selectLicencesAndOwners);
  const unassignedOnly = useSelector(selectOnlyUnassigned);
  const loading = useSelector(selectLicenseLoading);
  const modalLicense = useSelector(selectModalLicense);
  const [t] = useTranslation("global");
  const dispatch = useDispatch();

  const apiRef = useGridApiRef();
  const { setFilterModel, setShowFilterForm, setPreselectedFilterField } =
    SetActions();
  const [quickFilterValue, setQuickFilterValue] = useState("");

  const handleQuickFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setQuickFilterValue(value);

    if (apiRef.current) {
      apiRef.current.setQuickFilterValues(value ? [value] : []);
    }
  };

  const { licenseColumns } = useLicenseColumns(t, dispatch);
  const { filterModel, showFilterForm, preselectedFilterField } =
    useSelector(LicensesFilters);

  return (
    <Box>
      <div
        style={{
          ...customFilterStyles.filterContainer,
          marginLeft: "20px",
          marginTop: "20px",
        }}
      >
        <SearchComponent
          t={t}
          quickFilterValue={quickFilterValue}
          handleQuickFilterChange={handleQuickFilterChange}
        />
        <div style={DevicesStyles.containerFilterButon}>
          <CustomFilterButton
            t={t}
            isDisabled={false}
            loadingsValues={false}
            filterModel={filterModel}
            setShowFilterForm={setShowFilterForm}
            showFilterForm={showFilterForm}
            setPreselectedFilterField={setPreselectedFilterField}
            preselectedField={preselectedFilterField}
            columnsTable={licenseColumns as any}
            setFilterModel={setFilterModel}
            handleApplyFilters={() => {}}
            hasNoData={false}
          />
        </div>
      </div>
      <ThemeProvider theme={themeInarix(locale)}>
        <div className="container-licenses-table">
          <Box sx={{ height: "67vh", typography: "body1" }}>
            <DataGridPro
              apiRef={apiRef}
              rows={
                unassignedOnly
                  ? licenses.filter((l) => !("username" in l))
                  : licenses
              }
              columns={licenseColumns}
              rowHeight={70}
              pagination
              disableColumnSelector={true}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              loading={loading}
              pageSizeOptions={[25, 50, 100]}
            />
          </Box>
        </div>
        <Modal
          open={!!modalLicense}
          onClose={() => dispatch(focusedLicense(""))}
        >
          <LicensePacksModal />
        </Modal>
      </ThemeProvider>
    </Box>
  );
};
